"use client";

import { useMemo } from "react";

export const PASSWORD_CHECKS = [
  { label: "8+ characters", hint: "Add more characters", test: (p: string) => p.length >= 8 },
  { label: "Uppercase", hint: "Add an uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Lowercase", hint: "Add a lowercase letter", test: (p: string) => /[a-z]/.test(p) },
  { label: "Number", hint: "Add a number", test: (p: string) => /\d/.test(p) },
  { label: "Special char", hint: "Add a special character (!@#$...)", test: (p: string) => /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'`~]/.test(p) },
];

/** Returns the hint for the first unmet requirement, or null if all pass. */
export function validatePasswordFully(password: string): string | null {
  for (const check of PASSWORD_CHECKS) {
    if (!check.test(password)) return check.hint;
  }
  return null;
}

interface PasswordStrengthProps {
  password: string;
}

export default function PasswordStrength({ password }: PasswordStrengthProps) {
  const { score, firstHint } = useMemo(() => {
    let s = 0;
    let hint: string | null = null;
    for (const check of PASSWORD_CHECKS) {
      if (check.test(password)) {
        s++;
      } else if (!hint) {
        hint = check.hint;
      }
    }
    return { score: s, firstHint: hint };
  }, [password]);

  if (!password) return null;

  const strength =
    score <= 2 ? { label: "Weak", color: "bg-red-500", textColor: "text-red-600" } :
    score <= 3 ? { label: "Fair", color: "bg-amber-500", textColor: "text-amber-600" } :
    score === 4 ? { label: "Strong", color: "bg-emerald-500", textColor: "text-emerald-600" } :
    { label: "Excellent", color: "bg-emerald-500", textColor: "text-emerald-600" };

  return (
    <div className="mt-2 space-y-1.5">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex gap-0.5 flex-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i < score ? strength.color : "bg-slate-200"
              }`}
            />
          ))}
        </div>
        <span className={`text-xs font-medium shrink-0 ${strength.textColor}`}>
          {strength.label} {score}/5
        </span>
      </div>

      {/* Single-line dynamic hint */}
      {firstHint && (
        <p className="text-xs text-slate-500">{firstHint}</p>
      )}
    </div>
  );
}
